import { IamGroupType, type SmsListApiData } from '@navio/nsdk';
import { filterUnitFor, filterUrlState, type QuickFilterOption, useFilterUnits } from 'noddi-ui-web';
import { CURRENCY_OPTIONS, SMS_TYPE_OPTIONS } from 'noddi-util';
import { useAccountOptions } from '../Account/useAccountFilter';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import { useServiceDepartmentOptions } from '../ServiceDepartment/useServiceDepartmentFilter';
import { useUserGroupOptions } from '../UserGroup/useUserGroupFilter';
import { SMS_STATUS_FILTER_OPTIONS } from './useSMSStatusesFilter';

type SmsListFilterInput = Omit<NonNullable<SmsListApiData['query']>, 'pageIndex' | 'pageSize' | 'ordering' | 'search'>;

const smsFilterUnit = filterUnitFor<SmsListFilterInput>();

export const useSMSTableFilters = () => {
  // Each control searches its own options.
  const receivers = useAccountOptions();
  const senders = useAccountOptions();
  const groups = useUserGroupOptions({ types: [IamGroupType.GROUP] });
  const serviceDepartments = useServiceDepartmentOptions();

  return useFilterUnits([
    smsFilterUnit.multiSelect('typeIn', {
      title: 'Message type',
      description: 'Filter by message type',
      options: SMS_TYPE_OPTIONS,
      exclude: true,
      defaultVisibleOptions: 15
    }),
    // One status picked at a time, as before; `smsStatuses` is the param whose exclusions match it.
    smsFilterUnit.multiSelect('smsStatuses', {
      title: 'Status',
      description: 'Filter by SMS delivery status',
      options: SMS_STATUS_FILTER_OPTIONS,
      single: true,
      exclude: true,
      defaultVisibleOptions: 15
    }),
    smsFilterUnit.multiSelect('receiverUserIds', {
      title: 'Receiver',
      description: 'Filter by receiver',
      source: receivers,
      exclude: true
    }),
    smsFilterUnit.multiSelect('userGroupIds', {
      title: 'Groups',
      description: 'Filter by specific groups',
      source: groups,
      defaultVisibleOptions: 3,
      exclude: true
    }),
    smsFilterUnit.multiSelect('serviceDepartmentIds', {
      title: 'Service departments',
      description: 'Filter by specific service departments or locations',
      source: serviceDepartments,
      exclude: true
    }),
    smsFilterUnit.multiSelect('senderUserIds', {
      title: 'Sender',
      description: 'Filter by sender',
      source: senders,
      exclude: true
    }),
    smsFilterUnit.boolean('hasPrice', { title: 'Has price', description: 'Filter SMS messages that have a price set' }),
    // Twilio's price in the currency's major unit (0.0079 USD), not øre: no `money` conversion.
    smsFilterUnit.numberRange('price', {
      title: 'Price',
      description: 'Filter by price range',
      minLabel: 'Min price',
      maxLabel: 'Max price',
      exclude: true
    }),
    smsFilterUnit.multiSelect('priceCurrencyIn', {
      title: 'Price currency',
      description: 'Filter by price currency',
      options: CURRENCY_OPTIONS,
      exclude: true
    }),
    smsFilterUnit.numberRange('numSegments', {
      title: 'Number of segments',
      description: 'Filter by number of SMS segments',
      minLabel: 'Min segments',
      maxLabel: 'Max segments',
      exclude: true
    }),
    smsFilterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
    smsFilterUnit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true }),
    smsFilterUnit.dateRange('sentAt', { title: 'Sent at', description: 'Filter by sent date range', exclude: true }),
    smsFilterUnit.dateRange('scheduledAt', {
      title: 'Scheduled at',
      description: 'Filter by scheduled date range',
      exclude: true
    }),
    smsFilterUnit.dateRange('failedAt', {
      title: 'Failed at',
      description: 'Filter by failed date range',
      exclude: true
    }),
    smsFilterUnit.dateRange('deliveredAt', {
      title: 'Delivered at',
      description: 'Filter by delivered date range',
      exclude: true
    }),
    smsFilterUnit.dateRange('cancelledAt', {
      title: 'Cancelled at',
      description: 'Filter by cancelled date range',
      exclude: true
    }),
    smsRetryQueueFilter
  ]);
};

const RETRY_SCOPE_URL_KEY = 'sms-retry-scope';

type RetryScope = 'all' | 'retry-queue' | 'needs-attention';

const RETRY_SCOPE_OPTIONS: readonly [
  QuickFilterOption<RetryScope>,
  QuickFilterOption<RetryScope>,
  QuickFilterOption<RetryScope>
] = [
  { value: 'all', label: 'All' },
  { value: 'retry-queue', label: 'Retry queue' },
  { value: 'needs-attention', label: 'Needs attention' }
];

const isRetryScope = (value: string): value is RetryScope =>
  RETRY_SCOPE_OPTIONS.some((option) => option.value === value);

/** The retry-queue tray: one choice writing one of two flags, which no builder expresses. */
const smsRetryQueueFilter = smsFilterUnit.compose(
  'sms-retry-scope',
  {
    [RETRY_SCOPE_URL_KEY]: filterUrlState.stored<RetryScope>(String, (value) =>
      isRetryScope(value) ? value : undefined
    )
  },
  (values, setValues) => {
    const scope = filterUrlState.storedValue(values[RETRY_SCOPE_URL_KEY] as RetryScope | null | undefined, 'all');
    const selected = scope ?? 'all';
    return {
      controls: {
        quickFilter: {
          label: 'Retry queue',
          options: RETRY_SCOPE_OPTIONS,
          selectedValue: selected,
          onChange: (value: string) =>
            setValues(() => ({ [RETRY_SCOPE_URL_KEY]: value === 'all' || !isRetryScope(value) ? undefined : value }))
        }
      },
      params: {
        unacceptedInRetryQueue: selected === 'retry-queue' ? true : undefined,
        unacceptedRetriesExhausted: selected === 'needs-attention' ? true : undefined
      }
    };
  }
);
